import { normalizeLocale, type SupportedLocale } from './config';

/** Copy for the Connect search scope toggle, results, and filters. */
type ConnectSearchCopy = {
  titleAll: string;
  description: string;
  placeholderAll: string;
  /** Command menu (Cmd+K) group heading for Connect message hits. */
  messagesHeading: string;
  scopeChannel: string;
  scopeAll: string;
  threadReply: string;
  directMessage: string;
  noChannel: (slug: string) => string;
  noPerson: (name: string) => string;
  more: string;
};

const COPY: Record<SupportedLocale, ConnectSearchCopy> = {
  en: {
    titleAll: 'Search Connect',
    description:
      'Search messages and thread replies. Filter with in:#channel and from:@person.',
    placeholderAll: 'Search… try in:#channel or from:@person',
    messagesHeading: 'Messages',
    scopeChannel: 'This channel',
    scopeAll: 'All of Connect',
    threadReply: 'Thread reply',
    directMessage: 'Direct message',
    noChannel: (s) => `No channel you can read matches #${s}.`,
    noPerson: (n) => `No workspace member matches @${n}.`,
    more: 'Showing the newest 40 matches. Add a filter to narrow it down.',
  },
  'pt-BR': {
    titleAll: 'Pesquisar no Connect',
    description:
      'Pesquise mensagens e respostas em threads. Filtre com in:#canal e from:@pessoa.',
    placeholderAll: 'Pesquisar… tente in:#canal ou from:@pessoa',
    messagesHeading: 'Mensagens',
    scopeChannel: 'Este canal',
    scopeAll: 'Todo o Connect',
    threadReply: 'Resposta em thread',
    directMessage: 'Mensagem direta',
    noChannel: (s) => `Nenhum canal que você pode ler corresponde a #${s}.`,
    noPerson: (n) => `Nenhum membro do workspace corresponde a @${n}.`,
    more: 'Mostrando as 40 correspondências mais recentes. Adicione um filtro para refinar.',
  },
  ar: {
    titleAll: 'البحث في Connect',
    description:
      'ابحث في الرسائل وردود المواضيع. صفِّ النتائج باستخدام in:#القناة و from:@الشخص.',
    placeholderAll: 'ابحث… جرّب in:#القناة أو from:@الشخص',
    messagesHeading: 'الرسائل',
    scopeChannel: 'هذه القناة',
    scopeAll: 'كل Connect',
    threadReply: 'رد في موضوع',
    directMessage: 'رسالة مباشرة',
    noChannel: (s) => `لا توجد قناة يمكنك قراءتها تطابق #${s}.`,
    noPerson: (n) => `لا يوجد عضو في مساحة العمل يطابق @${n}.`,
    more: 'عرض أحدث 40 نتيجة. أضف عامل تصفية لتضييق النتائج.',
  },
  es: {
    titleAll: 'Buscar en Connect',
    description:
      'Busca mensajes y respuestas en hilos. Filtra con in:#canal y from:@persona.',
    placeholderAll: 'Buscar… prueba in:#canal o from:@persona',
    messagesHeading: 'Mensajes',
    scopeChannel: 'Este canal',
    scopeAll: 'Todo Connect',
    threadReply: 'Respuesta en hilo',
    directMessage: 'Mensaje directo',
    noChannel: (s) => `Ningún canal que puedas leer coincide con #${s}.`,
    noPerson: (n) => `Ningún miembro del espacio coincide con @${n}.`,
    more: 'Se muestran las 40 coincidencias más recientes. Añade un filtro para acotar.',
  },
  fr: {
    titleAll: 'Rechercher dans Connect',
    description:
      'Recherchez les messages et les réponses de fil. Filtrez avec in:#canal et from:@personne.',
    placeholderAll: 'Rechercher… essayez in:#canal ou from:@personne',
    messagesHeading: 'Messages',
    scopeChannel: 'Ce canal',
    scopeAll: 'Tout Connect',
    threadReply: 'Réponse dans un fil',
    directMessage: 'Message direct',
    noChannel: (s) => `Aucun canal accessible ne correspond à #${s}.`,
    noPerson: (n) => `Aucun membre de l’espace ne correspond à @${n}.`,
    more: 'Affichage des 40 résultats les plus récents. Ajoutez un filtre pour affiner.',
  },
  it: {
    titleAll: 'Cerca in Connect',
    description:
      'Cerca messaggi e risposte nei thread. Filtra con in:#canale e from:@persona.',
    placeholderAll: 'Cerca… prova in:#canale o from:@persona',
    messagesHeading: 'Messaggi',
    scopeChannel: 'Questo canale',
    scopeAll: 'Tutto Connect',
    threadReply: 'Risposta nel thread',
    directMessage: 'Messaggio diretto',
    noChannel: (s) => `Nessun canale leggibile corrisponde a #${s}.`,
    noPerson: (n) => `Nessun membro dello spazio corrisponde a @${n}.`,
    more: 'Mostro le 40 corrispondenze più recenti. Aggiungi un filtro per restringere.',
  },
  ko: {
    titleAll: 'Connect 검색',
    description:
      '메시지와 스레드 답글을 검색합니다. in:#채널, from:@사람으로 필터링하세요.',
    placeholderAll: '검색… in:#채널 또는 from:@사람을 사용해 보세요',
    messagesHeading: '메시지',
    scopeChannel: '이 채널',
    scopeAll: 'Connect 전체',
    threadReply: '스레드 답글',
    directMessage: '다이렉트 메시지',
    noChannel: (s) => `읽을 수 있는 채널 중 #${s}와 일치하는 채널이 없습니다.`,
    noPerson: (n) => `@${n}와 일치하는 워크스페이스 멤버가 없습니다.`,
    more: '최근 결과 40개를 표시합니다. 필터를 추가해 범위를 좁히세요.',
  },
  de: {
    titleAll: 'Connect durchsuchen',
    description:
      'Durchsuche Nachrichten und Thread-Antworten. Filtere mit in:#kanal und from:@person.',
    placeholderAll: 'Suchen… z. B. in:#kanal oder from:@person',
    messagesHeading: 'Nachrichten',
    scopeChannel: 'Dieser Kanal',
    scopeAll: 'Ganz Connect',
    threadReply: 'Thread-Antwort',
    directMessage: 'Direktnachricht',
    noChannel: (s) => `Kein lesbarer Kanal passt zu #${s}.`,
    noPerson: (n) => `Kein Workspace-Mitglied passt zu @${n}.`,
    more: 'Die 40 neuesten Treffer werden angezeigt. Füge einen Filter hinzu, um einzugrenzen.',
  },
  ja: {
    titleAll: 'Connect を検索',
    description:
      'メッセージとスレッドの返信を検索します。in:#チャンネル と from:@ユーザー で絞り込めます。',
    placeholderAll: '検索… in:#チャンネル や from:@ユーザー も使えます',
    messagesHeading: 'メッセージ',
    scopeChannel: 'このチャンネル',
    scopeAll: 'Connect 全体',
    threadReply: 'スレッドの返信',
    directMessage: 'ダイレクトメッセージ',
    noChannel: (s) => `#${s} に一致する閲覧可能なチャンネルはありません。`,
    noPerson: (n) => `@${n} に一致するメンバーはいません。`,
    more: '新しい順に 40 件を表示しています。フィルターで絞り込んでください。',
  },
  hi: {
    titleAll: 'Connect में खोजें',
    description:
      'संदेश और थ्रेड जवाब खोजें। in:#चैनल और from:@व्यक्ति से फ़िल्टर करें।',
    placeholderAll: 'खोजें… in:#चैनल या from:@व्यक्ति आज़माएँ',
    messagesHeading: 'संदेश',
    scopeChannel: 'यह चैनल',
    scopeAll: 'पूरा Connect',
    threadReply: 'थ्रेड जवाब',
    directMessage: 'डायरेक्ट मैसेज',
    noChannel: (s) => `आपके पढ़ने योग्य कोई चैनल #${s} से मेल नहीं खाता।`,
    noPerson: (n) => `कोई वर्कस्पेस सदस्य @${n} से मेल नहीं खाता।`,
    more: 'सबसे नए 40 परिणाम दिखाए जा रहे हैं। सीमित करने के लिए फ़िल्टर जोड़ें।',
  },
  ru: {
    titleAll: 'Поиск в Connect',
    description:
      'Ищите сообщения и ответы в тредах. Фильтры: in:#канал и from:@человек.',
    placeholderAll: 'Поиск… попробуйте in:#канал или from:@человек',
    messagesHeading: 'Сообщения',
    scopeChannel: 'Этот канал',
    scopeAll: 'Весь Connect',
    threadReply: 'Ответ в треде',
    directMessage: 'Личное сообщение',
    noChannel: (s) => `Нет доступного канала, совпадающего с #${s}.`,
    noPerson: (n) => `Нет участника, совпадающего с @${n}.`,
    more: 'Показаны 40 самых новых совпадений. Добавьте фильтр, чтобы сузить поиск.',
  },
  zh: {
    titleAll: '搜索 Connect',
    description: '搜索消息和话题回复。可用 in:#频道 和 from:@成员 筛选。',
    placeholderAll: '搜索… 试试 in:#频道 或 from:@成员',
    messagesHeading: '消息',
    scopeChannel: '此频道',
    scopeAll: '整个 Connect',
    threadReply: '话题回复',
    directMessage: '私信',
    noChannel: (s) => `没有你可阅读的频道匹配 #${s}。`,
    noPerson: (n) => `没有工作区成员匹配 @${n}。`,
    more: '显示最新的 40 条结果。添加筛选条件以缩小范围。',
  },
  'zh-TW': {
    titleAll: '搜尋 Connect',
    description: '搜尋訊息和討論串回覆。可用 in:#頻道 和 from:@成員 篩選。',
    placeholderAll: '搜尋… 試試 in:#頻道 或 from:@成員',
    messagesHeading: '訊息',
    scopeChannel: '此頻道',
    scopeAll: '整個 Connect',
    threadReply: '討論串回覆',
    directMessage: '私訊',
    noChannel: (s) => `沒有你可閱讀的頻道符合 #${s}。`,
    noPerson: (n) => `沒有工作區成員符合 @${n}。`,
    more: '顯示最新的 40 筆結果。新增篩選條件以縮小範圍。',
  },
  vi: {
    titleAll: 'Tìm kiếm Connect',
    description:
      'Tìm tin nhắn và trả lời trong luồng. Lọc bằng in:#kênh và from:@người.',
    placeholderAll: 'Tìm kiếm… thử in:#kênh hoặc from:@người',
    messagesHeading: 'Tin nhắn',
    scopeChannel: 'Kênh này',
    scopeAll: 'Toàn bộ Connect',
    threadReply: 'Trả lời trong luồng',
    directMessage: 'Tin nhắn trực tiếp',
    noChannel: (s) => `Không có kênh bạn đọc được khớp với #${s}.`,
    noPerson: (n) => `Không có thành viên nào khớp với @${n}.`,
    more: 'Đang hiển thị 40 kết quả mới nhất. Thêm bộ lọc để thu hẹp.',
  },
  id: {
    titleAll: 'Cari di Connect',
    description:
      'Cari pesan dan balasan utas. Saring dengan in:#kanal dan from:@orang.',
    placeholderAll: 'Cari… coba in:#kanal atau from:@orang',
    messagesHeading: 'Pesan',
    scopeChannel: 'Kanal ini',
    scopeAll: 'Seluruh Connect',
    threadReply: 'Balasan utas',
    directMessage: 'Pesan langsung',
    noChannel: (s) =>
      `Tidak ada kanal yang dapat Anda baca yang cocok dengan #${s}.`,
    noPerson: (n) => `Tidak ada anggota workspace yang cocok dengan @${n}.`,
    more: 'Menampilkan 40 hasil terbaru. Tambahkan filter untuk mempersempit.',
  },
};

export function getConnectSearchCopy(locale: string): ConnectSearchCopy {
  return COPY[normalizeLocale(locale)];
}
